import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import { translations, stepsTranslations } from '../translations/content';
import Auth from './Auth';
import supabase from '../supabase/supabase';

const {
  FiTarget, FiAward, FiLightbulb, FiUsers, FiCompass, FiShield,
  FiPackage, FiEdit3, FiTool, FiRocket, FiZap, FiArrowRight, FiMail, FiCheck, FiCheckCircle
} = FiIcons;

const stepIcons = [
  FiTarget, FiAward, FiLightbulb, FiUsers, FiCompass,
  FiShield, FiPackage, FiEdit3, FiTool, FiRocket
];

const stepColors = [
  { from: 'from-blue-500', to: 'to-cyan-400', bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  { from: 'from-purple-500', to: 'to-pink-400', bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
  { from: 'from-yellow-500', to: 'to-orange-400', bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-200' },
  { from: 'from-green-500', to: 'to-emerald-400', bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
  { from: 'from-indigo-500', to: 'to-blue-400', bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
  { from: 'from-red-500', to: 'to-pink-400', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
  { from: 'from-teal-500', to: 'to-cyan-400', bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200' },
  { from: 'from-orange-500', to: 'to-amber-400', bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
  { from: 'from-violet-500', to: 'to-purple-400', bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200' },
  { from: 'from-rose-500', to: 'to-pink-400', bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' }
];

const StepGraphic = ({ index, colors }) => {
  // Simple illustrative SVG graphics for each step
  const graphics = [
    // Step 1: Vision (Telescope/Target)
    <svg viewBox="0 0 100 100" className="w-full h-full opacity-20 group-hover:opacity-40 transition-opacity duration-500">
      <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
      <circle cx="50" cy="50" r="20" fill="currentColor" />
      <path d="M50 10 L50 30 M50 70 L50 90 M10 50 L30 50 M70 50 L90 50" stroke="currentColor" strokeWidth="4" />
    </svg>,
    // Step 2: Knowledge (Book/Brain)
    <svg viewBox="0 0 100 100" className="w-full h-full opacity-20 group-hover:opacity-40 transition-opacity duration-500">
      <rect x="20" y="20" width="60" height="60" rx="4" fill="none" stroke="currentColor" strokeWidth="4" />
      <line x1="30" y1="40" x2="70" y2="40" stroke="currentColor" strokeWidth="2" />
      <line x1="30" y1="50" x2="70" y2="50" stroke="currentColor" strokeWidth="2" />
      <line x1="30" y1="60" x2="50" y2="60" stroke="currentColor" strokeWidth="2" />
    </svg>,
    // Step 3: Idea (Lightbulb)
    <svg viewBox="0 0 100 100" className="w-full h-full opacity-20 group-hover:opacity-40 transition-opacity duration-500">
      <path d="M50 20 A25 25 0 1 1 50 70 L50 85 M40 85 L60 85" fill="none" stroke="currentColor" strokeWidth="4" />
      <path d="M35 35 L25 25 M65 35 L75 25 M50 15 L50 5" stroke="currentColor" strokeWidth="2" />
    </svg>,
    // Step 4: Audience (Users)
    <svg viewBox="0 0 100 100" className="w-full h-full opacity-20 group-hover:opacity-40 transition-opacity duration-500">
      <circle cx="35" cy="40" r="10" fill="currentColor" />
      <path d="M20 70 Q35 50 50 70" stroke="currentColor" strokeWidth="4" fill="none" />
      <circle cx="65" cy="40" r="10" fill="currentColor" />
      <path d="M50 70 Q65 50 80 70" stroke="currentColor" strokeWidth="4" fill="none" />
    </svg>,
    // Step 5: Niche (Compass/Magnifier)
    <svg viewBox="0 0 100 100" className="w-full h-full opacity-20 group-hover:opacity-40 transition-opacity duration-500">
      <circle cx="45" cy="45" r="25" fill="none" stroke="currentColor" strokeWidth="4" />
      <line x1="62" y1="62" x2="85" y2="85" stroke="currentColor" strokeWidth="8" strokeLinecap="round" />
      <circle cx="45" cy="45" r="5" fill="currentColor" />
    </svg>,
    // Step 6: Relevance (Magnet/Shield)
    <svg viewBox="0 0 100 100" className="w-full h-full opacity-20 group-hover:opacity-40 transition-opacity duration-500">
      <path d="M30 40 L30 70 A20 20 0 0 0 70 70 L70 40" fill="none" stroke="currentColor" strokeWidth="6" />
      <rect x="25" y="30" width="10" height="15" fill="currentColor" />
      <rect x="65" y="30" width="10" height="15" fill="currentColor" />
    </svg>,
    // Step 7: Offer (Gift/Package)
    <svg viewBox="0 0 100 100" className="w-full h-full opacity-20 group-hover:opacity-40 transition-opacity duration-500">
      <rect x="25" y="40" width="50" height="40" fill="none" stroke="currentColor" strokeWidth="4" />
      <path d="M25 40 L50 20 L75 40" stroke="currentColor" strokeWidth="4" fill="none" />
      <line x1="50" y1="40" x2="50" y2="80" stroke="currentColor" strokeWidth="2" />
    </svg>,
    // Step 8: Content (Pen/Paper)
    <svg viewBox="0 0 100 100" className="w-full h-full opacity-20 group-hover:opacity-40 transition-opacity duration-500">
      <rect x="20" y="20" width="60" height="60" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M70 30 L40 60 L30 70 L40 60" stroke="currentColor" strokeWidth="4" />
      <line x1="25" y1="35" x2="55" y2="35" stroke="currentColor" strokeWidth="2" />
      <line x1="25" y1="45" x2="65" y2="45" stroke="currentColor" strokeWidth="2" />
    </svg>,
    // Step 9: MVP (Tools/Brick)
    <svg viewBox="0 0 100 100" className="w-full h-full opacity-20 group-hover:opacity-40 transition-opacity duration-500">
      <rect x="20" y="50" width="30" height="20" fill="currentColor" />
      <rect x="55" y="50" width="30" height="20" fill="currentColor" />
      <rect x="37" y="25" width="30" height="20" fill="currentColor" />
    </svg>,
    // Step 10: Launch (Rocket)
    <svg viewBox="0 0 100 100" className="w-full h-full opacity-20 group-hover:opacity-40 transition-opacity duration-500">
      <path d="M50 15 Q70 50 50 85 Q30 50 50 15" fill="currentColor" />
      <path d="M40 70 L30 85 M60 70 L70 85" stroke="currentColor" strokeWidth="4" />
    </svg>
  ];

  return (
    <div className={`absolute -right-10 -bottom-10 w-48 h-48 ${colors.text}`}>
      {graphics[index] || graphics[0]}
    </div>
  );
};

const StepsOverview = ({ steps: propsSteps }) => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const t = translations[lang].overview;
  const p = translations[lang].paths;
  const [session, setSession] = useState(null);
  
  const steps = propsSteps || stepsTranslations[lang];

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-0 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100 -z-10" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-block mb-6">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-black uppercase tracking-widest shadow-lg">
              <SafeIcon icon={FiTarget} className="mr-2" />
              {t.step} 1-10
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black text-gray-900 mb-6 tracking-tight leading-tight uppercase">
            {t.title}
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto font-medium leading-relaxed">
            {t.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps && steps.map((step, index) => {
            const colors = stepColors[index] || stepColors[0];
            const Icon = stepIcons[index] || FiTarget;
            
            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="group relative"
              >
                <Link to={`${p.steps}/${step.id}`} className="block">
                  <div className={`relative bg-white rounded-[2rem] p-8 shadow-sm border-2 ${colors.border} hover:shadow-2xl transition-all duration-300 overflow-hidden h-full`}>
                    <StepGraphic index={index} colors={colors} />
                    
                    <div className="relative z-10">
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${colors.bg} ${colors.text} mb-6`}>
                        <SafeIcon icon={Icon} className="text-3xl" />
                      </div>
                      <h3 className="text-2xl font-black text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-600 font-medium leading-relaxed mb-6">{step.subtitle}</p>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <span className="text-sm font-bold text-gray-400 group-hover:text-gray-600 transition-colors uppercase tracking-wider">{t.learnMore}</span>
                        <div className={`w-8 h-8 rounded-full ${colors.bg} ${colors.text} flex items-center justify-center`}>
                          <SafeIcon icon={FiArrowRight} className="text-sm" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Simplified Registration Section - Replaced legacy section */}
      <section id="registration-section" className="py-24 bg-gray-50 overflow-hidden border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight leading-tight uppercase">
                {lang === 'de' ? 'Bereit für den ersten Schritt?' : 'Ready for the first step?'}
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed font-medium">
                {lang === 'de'
                  ? 'Erhalte sofortigen Zugriff auf alle 10 Schritte und transformiere deine Idee in ein echtes Business.'
                  : 'Get instant access to all 10 steps and transform your idea into a real business.'
                }
              </p>
              
              <ul className="space-y-4">
                {[
                  lang === 'de' ? 'Kostenloser Account' : 'Free Account',
                  lang === 'de' ? 'Alle 10 Schritte freigeschaltet' : 'All 10 steps unlocked',
                  lang === 'de' ? 'Interaktive Strategie-Tools' : 'Interactive strategy tools'
                ].map((item, i) => (
                  <li key={i} className="flex items-center space-x-3 text-gray-700 font-bold">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                      <SafeIcon icon={FiCheck} className="text-xs" />
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Decorative background blur */}
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-[3rem] opacity-20 blur-2xl"></div>
              
              <div className="relative">
                {session ? (
                  <div className="bg-white p-12 rounded-[2.5rem] shadow-2xl border border-gray-100 text-center">
                    <div className="w-20 h-20 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon={FiCheckCircle} className="text-4xl text-green-500" />
                    </div>
                    <h3 className="text-2xl font-black text-gray-900 mb-4 uppercase tracking-tight">
                      {lang === 'de' ? 'Willkommen zurück!' : 'Welcome Back!'}
                    </h3>
                    <p className="text-gray-600 mb-8 font-medium">
                      {lang === 'de' 
                        ? 'Du bist bereits angemeldet und hast vollen Zugriff.' 
                        : 'You are already signed in and have full access.'}
                    </p>
                    <button 
                      onClick={() => navigate(lang === 'de' ? '/schritte' : '/steps')}
                      className="w-full bg-blue-600 text-white font-black py-4 rounded-xl shadow-lg hover:bg-blue-700 transition-all uppercase tracking-widest"
                    >
                      {lang === 'de' ? 'Zu den Schritten' : 'Go to Steps'}
                    </button>
                  </div>
                ) : (
                  <Auth embedded={false} initialIsLogin={false} />
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StepsOverview;